<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { dispatchWithAlert } from 'dashboard/helper/dispatchWithAlert';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
// Agenda jurídica, não o módulo de agendamentos. O AppointmentBookingModal
// continua no repositório e intocado — apenas não é mais aberto por este botão.
// Medido em produção 02/09: appointments=0, profissionais=0, servicos=0 contra
// 4.539 atividades na agenda. O botão pedia um cadastro que não existe.
import ScheduleActivityModal from './ScheduleActivityModal.vue';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { etiquetaCasa, nomeDaEtiqueta } from 'dashboard/helper/nomeDaEtiqueta';

const store = useStore();
const { t } = useI18n();

// Label quick actions
const {
  accountLabels,
  savedLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

const showLabelDropdown = ref(false);
const labelSearch = ref('');

const filteredLabels = computed(() => {
  const q = labelSearch.value;
  return q
    ? accountLabels.value.filter(l => etiquetaCasa(l.title, q))
    : accountLabels.value;
});

const isLabelActive = title => savedLabels.value.includes(title);

const toggleLabel = title => {
  if (isLabelActive(title)) {
    removeLabelFromConversation(title);
  } else {
    addLabelToConversation({ title });
  }
};

const currentChat = computed(() => store.getters.getSelectedChat);

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const showBookingModal = ref(false);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // "Pausar/Retomar IA" agora é ícone direto no header da conversa
  // (ConversationHeader → toggleAiPause) — fora do menu p/ não duplicar.

  // "Agendar Atendimento" agora é ícone direto no header da conversa
  // (ConversationHeader → OPEN_APPOINTMENT_BOOKING) — fora do menu p/ não duplicar.

  return items;
});

// Definidas antes de `handleActionClick` por causa do no-use-before-define.
// Também usadas pelos event listeners de atalho — o menu delega pra cá em vez
// de duplicar o dispatch (eram 2 cópias que precisavam ser corrigidas juntas).
const mute = () => {
  dispatchWithAlert(store.dispatch('muteConversation', currentChat.value.id), {
    success: t('CONTACT_PANEL.MUTED_SUCCESS'),
    failure: t('CONTACT_PANEL.MUTED_FAILED'),
  });
};

const unmute = () => {
  dispatchWithAlert(
    store.dispatch('unmuteConversation', currentChat.value.id),
    {
      success: t('CONTACT_PANEL.UNMUTED_SUCCESS'),
      failure: t('CONTACT_PANEL.UNMUTED_FAILED'),
    }
  );
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    mute();
  } else if (action === 'unmute') {
    unmute();
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'book_appointment') {
    showBookingModal.value = true;
  }
};

const openBookingModal = () => {
  showBookingModal.value = true;
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);
emitter.on(BUS_EVENTS.OPEN_APPOINTMENT_BOOKING, openBookingModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  emitter.off(BUS_EVENTS.OPEN_APPOINTMENT_BOOKING, openBookingModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />

    <!-- Label quick action -->
    <div
      v-on-clickaway="
        () => {
          showLabelDropdown = false;
          labelSearch = '';
        }
      "
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.LABEL_ACTION')"
        :aria-label="$t('CONVERSATION.HEADER.LABEL_ACTION')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-tag"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="showLabelDropdown = !showLabelDropdown"
      />
      <Transition name="advup-pop">
        <div
          v-if="showLabelDropdown"
          class="absolute top-full mt-1 ltr:right-0 rtl:left-0 z-50 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl shadow-lg w-56 overflow-hidden advup-pop"
        >
          <div class="p-2 border-b border-n-weak">
            <input
              v-model="labelSearch"
              type="text"
              :placeholder="$t('CONTACT_PANEL.SEARCH_LABEL_PLACEHOLDER')"
              class="w-full text-xs bg-transparent text-n-slate-12 placeholder-n-slate-9 outline-none px-2 py-1 reset-base"
            />
          </div>
          <div class="max-h-48 overflow-y-auto py-1">
            <button
              v-for="label in filteredLabels"
              :key="label.id"
              class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="toggleLabel(label.title)"
            >
              <span
                class="size-2 rounded-full flex-shrink-0"
                :style="{ background: label.color }"
              />
              <span
                class="flex-1 truncate text-left"
                :title="nomeDaEtiqueta(label.title)"
              >
                {{ nomeDaEtiqueta(label.title) }}
              </span>
              <span
                v-if="isLabelActive(label.title)"
                class="i-lucide-check size-3 text-n-blue-11 flex-shrink-0"
              />
            </button>
            <p
              v-if="!filteredLabels.length"
              class="px-3 py-2 text-xs text-n-slate-9"
            >
              {{ $t('CONTACT_PANEL.NO_LABELS_FOUND') }}
            </p>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Transferência agora é o modal "Transferir atendimento" no header
         (ConversationHeader → TransferConversationModal). -->

    <!-- Existing more actions dropdown -->
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <!-- Entra e sai como todo flutuante da casa (24/10/2026, `.advup-pop`). -->
      <Transition name="advup-pop">
        <DropdownMenu
          v-if="showActionsDropdown"
          :menu-items="actionMenuItems"
          class="mt-1 ltr:right-0 rtl:left-0 top-full"
          @action="handleActionClick"
        />
      </Transition>
    </div>
    <!-- Sem `v-if`: o próprio modal decide pelo `show` e sai pelo mesmo caminho
         (24/10/2026). Com o `v-if` aqui, a saída era engolida. -->
    <EmailTranscriptModal
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <!-- Sai pelo mesmo caminho por onde entrou (24/10/2026, `_advup-modal.scss`). -->
    <Transition name="advup-saida">
      <ScheduleActivityModal
        v-if="showBookingModal"
        :conversation-id="currentChat.id"
        :contact="currentChat.meta?.sender || {}"
        @close="showBookingModal = false"
      />
    </Transition>
  </div>
</template>
